import React, { useState, useEffect, useRef } from 'react';
import { styled, SxProps } from '@mui/material';
import { spacing, SpacingProps } from '@mui/system';
import EDButton from '../Buttons/Button/EDButton';
import EDTypography from '../Typography/EDTypography';
import EDIcon from '../Icon/EDIcon';
import EDBox from '../EDBox/EDBox';
import { EDTextEdit } from '../TextEdit/EDTextEdit';

export interface IChatMessage {
  id: string;
  text: string;
  sender: 'user' | string;
}

export interface IProps extends SpacingProps {
  messages?: IChatMessage[];
  onSendMessage?: (message: string) => void;
  placeholder?: string;
  disabled?: boolean;
  height?: number | string;
  testId?: string;
}

const conversationStyle: SxProps = {
  flex: 1,
  overflowY: 'auto',
  padding: 2,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  backgroundColor: 'background.paper',
};

const messageContainerStyle: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
};

const messageStyle = (isUser: boolean): SxProps => ({
  display: 'flex',
  justifyContent: isUser ? 'flex-end' : 'flex-start',
  marginBottom: 1,
});

const messageBubbleStyle = (isUser: boolean): SxProps => ({
  maxWidth: '70%',
  padding: 1.5,
  borderRadius: 2,
  backgroundColor: isUser ? 'primary.main' : 'grey.100',
  color: isUser ? 'primary.contrastText' : 'text.primary',
});

const inputContainerStyle: SxProps = {
  display: 'flex',
  gap: 1,
  alignItems: 'flex-end',
  marginTop: 2,
};

export const EDChatBase: React.FC<IProps & { className?: string }> = props => {
  const [inputMessage, setInputMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [props.messages]);

  const handleSendMessage = () => {
    if (inputMessage.trim() && props.onSendMessage) {
      props.onSendMessage(inputMessage.trim());
      setInputMessage('');
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <EDBox
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: props.height || 400,
        width: '100%',
      }}
      data-testid={props.testId}
      className={props.className}
    >
      <EDBox sx={conversationStyle}>
        <EDBox sx={messageContainerStyle}>
          {(props.messages || []).map(message => (
            <EDBox
              key={message.id}
              sx={messageStyle(message.sender === 'user')}
            >
              <EDBox sx={messageBubbleStyle(message.sender === 'user')}>
                <EDTypography
                  variant="body2"
                  color="inherit"
                  sx={{ whiteSpace: 'pre-wrap' }}
                >
                  {message.text}
                </EDTypography>
              </EDBox>
            </EDBox>
          ))}
          <div ref={messagesEndRef} />
        </EDBox>
      </EDBox>

      <EDBox sx={inputContainerStyle}>
        <EDTextEdit
          fullWidth
          multiline
          maxRows={3}
          value={inputMessage}
          onChange={setInputMessage}
          onKeyDown={handleKeyPress}
          placeholder={props.placeholder || 'Skriv ditt meddelande här...'}
          disabled={props.disabled}
          allMargins="dense"
        />
        <EDButton
          icon={<EDIcon iconType="send" color="inherit" />}
          onClick={handleSendMessage}
          disabled={props.disabled || !inputMessage.trim()}
          primary
          marginBottom={0.5}
        />
      </EDBox>
    </EDBox>
  );
};

const EDChat: React.FC<IProps> = styled(EDChatBase, {
  name: 'EDChat',
})(spacing);

export default EDChat;
